import { useState } from 'react';
import { Icon, type IconName } from './Icon';

export interface ActionItem {
  key: string;
  label: string;
  icon: IconName;
  onClick: () => void;
  /** Azione distruttiva: renderizzata in fondo, separata, rossa */
  danger?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

/**
 * Menu azioni condiviso (pattern P1/P3 del design system): un solo bottone
 * "Azioni"/kebab per le azioni secondarie di un header. Le voci danger vanno
 * in fondo, separate. `excludeKey` nasconde la voce già promossa altrove
 * (es. nella NextActionCard) così nessuna azione appare due volte.
 */
export function ActionsMenu({
  items,
  variant = 'ghost',
  label,
  excludeKey,
  disabled,
}: {
  items: ActionItem[];
  variant?: 'primary' | 'ghost' | 'default';
  label?: string;
  excludeKey?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const visible = items.filter((it) => !it.hidden && it.key !== excludeKey);
  if (!visible.length) return null;
  const normal = visible.filter((it) => !it.danger);
  const dangers = visible.filter((it) => it.danger);

  const btnClass = variant === 'primary' ? 'btn btn-primary' : variant === 'ghost' ? 'btn btn-ghost btn-icon' : 'btn';

  return (
    <div className="relative actions-menu">
      <button className={btnClass} onClick={() => setOpen((v) => !v)} disabled={disabled} aria-haspopup="menu" aria-expanded={open} title={label ?? 'Azioni'}>
        {variant === 'ghost'
          ? <Icon name="more" size={14} />
          : <>{label ?? 'Azioni'} <Icon name="chevronD" size={10} /></>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60] actions-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] border border-line bg-white shadow-lg overflow-hidden actions-menu-pop" style={{ minWidth: 230, borderRadius: 'var(--r-card)' }} role="menu">
            {normal.map((it) => (
              <div
                key={it.key}
                className={`flex items-center gap-2.5 px-3 py-2.5 hover:bg-paper-2 cursor-pointer transition text-[13px] actions-menu-row ${it.disabled ? 'opacity-50 pointer-events-none' : ''}`}
                role="menuitem"
                onClick={() => { setOpen(false); it.onClick(); }}
              >
                <Icon name={it.icon} size={12} className="mute" />
                <span>{it.label}</span>
              </div>
            ))}
            {dangers.length > 0 && (
              <div className="border-t border-line">
                {dangers.map((it) => (
                  <div
                    key={it.key}
                    className={`flex items-center gap-2.5 px-3 py-2.5 hover:bg-alert-bg cursor-pointer transition text-[13px] text-alert actions-menu-row ${it.disabled ? 'opacity-50 pointer-events-none' : ''}`}
                    role="menuitem"
                    onClick={() => { setOpen(false); it.onClick(); }}
                  >
                    <Icon name={it.icon} size={12} />
                    <span>{it.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
